import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  BookOpen, Search, ChevronDown, ExternalLink, FileDown, PlayCircle, Code2, Check,
  Image as ImageIcon, Music, FolderOpen,
} from "lucide-react";

const KIND_ICON = { link: ExternalLink, pdf: FileDown, video: PlayCircle, embed: Code2, file: FileDown, image: ImageIcon, audio: Music };
const PILL = "rounded-full text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 shrink-0";

/** Public / staff-only marker shown on categories, subcategories and items. */
const VisTag = ({ staff, staffCount = 0, testId }) => {
  if (staff) {
    return <span className={`${PILL} bg-amber-100 text-amber-700`} data-testid={testId}>Interno</span>;
  }
  return (
    <>
      <span className={`${PILL} bg-emerald-100 text-emerald-700`} data-testid={testId}>Público</span>
      {staffCount > 0 && (
        <span className={`${PILL} bg-amber-100 text-amber-700`}>+{staffCount} interno{staffCount === 1 ? "" : "s"}</span>
      )}
    </>
  );
};

/** Two-level (category → subcategory) multi-select of study resources. */
export const ResourcePicker = ({ open, onOpenChange, selected = [], onConfirm, excludeIds = [] }) => {
  const [catalog, setCatalog] = useState([]);
  const [cats, setCats] = useState([]);
  const [picked, setPicked] = useState(selected);
  const [q, setQ] = useState("");
  const [openCats, setOpenCats] = useState({});
  const [openSubs, setOpenSubs] = useState({});

  useEffect(() => {
    if (!open) return;
    setPicked(selected);
    setQ("");
    api.get("/resources").then((r) => setCatalog(r.data || [])).catch(() => setCatalog([]));
    api.get("/resource-categories").then((r) => setCats(r.data || [])).catch(() => setCats([]));
    /* eslint-disable-next-line */
  }, [open]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const skip = new Set(excludeIds);
    const subById = new Map(cats.filter((c) => c.parent_id).map((c) => [c.id, c]));
    const topByName = new Map(cats.filter((c) => !c.parent_id).map((c) => [c.name, c]));
    const map = new Map(); // category name → { name, order, loose: [], subs: Map }

    const bucket = (name) => {
      if (!map.has(name)) {
        const top = topByName.get(name);
        map.set(name, {
          name, order: top?.sort_order ?? 9999, staff: (top?.visibility || "all") === "staff",
          loose: [], subs: new Map(),
        });
      }
      return map.get(name);
    };

    for (const r of catalog) {
      if (skip.has(r.id) || r.published === false) continue;
      const sub = r.subcategory_id ? subById.get(r.subcategory_id) : null;
      const hay = `${r.title} ${r.description || ""} ${r.category} ${sub?.name || ""}`.toLowerCase();
      if (needle && !hay.includes(needle)) continue;
      const g = bucket(r.category || "Otros");
      if (sub) {
        if (!g.subs.has(sub.id)) g.subs.set(sub.id, {
          id: sub.id, name: sub.name, order: sub.sort_order ?? 9999,
          staff: (sub.visibility || "all") === "staff", items: [],
        });
        g.subs.get(sub.id).items.push(r);
      } else {
        g.loose.push(r);
      }
    }

    return [...map.values()]
      .map((g) => {
        const subs = [...g.subs.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
        const all = [...g.loose, ...subs.flatMap((s) => s.items)];
        return {
          ...g,
          subs: subs.map((s) => ({ ...s, staffCount: s.staff ? s.items.length : s.items.filter((r) => r.visibility === "staff").length })),
          total: all.length,
          staffCount: g.staff ? all.length : all.filter((r) => r.visibility === "staff").length
            + subs.filter((s) => s.staff).reduce((n, s) => n + s.items.length, 0),
        };
      })
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
  }, [catalog, cats, q, excludeIds]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const searching = q.trim() !== "";

  const renderItems = (items) => items.map((r) => {
    const Icon = KIND_ICON[r.kind] || ExternalLink;
    const on = picked.includes(r.id);
    return (
      <button
        type="button"
        key={r.id}
        onClick={() => toggle(r.id)}
        className={`w-full flex items-center gap-2.5 py-1.5 px-2 rounded-md text-left transition-colors ${on ? "bg-brand/10" : "hover:bg-slate-50"}`}
        data-testid={`picker-item-${r.id}`}
      >
        <span className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center ${on ? "bg-brand border-brand" : "border-slate-300"}`}>
          {on && <Check className="h-3 w-3 text-white" />}
        </span>
        <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm text-slate-800 truncate">{r.title}</span>
        </span>
        {r.visibility === "staff" && (
          <span className={`${PILL} bg-amber-100 text-amber-700`} data-testid={`picker-item-vis-${r.id}`}>Interno</span>
        )}
        <span className="text-[10px] font-bold text-slate-500 shrink-0">
          {r.level === "all" ? "TODOS" : r.level}
        </span>
      </button>
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="resource-picker-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Elegir material de Recursos</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Buscar en todas las categorías..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="resource-picker-search" />
        </div>

        <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1">
          {groups.map((g) => {
            const isOpen = searching || openCats[g.name];
            const allItems = [...g.loose, ...g.subs.flatMap((s) => s.items)];
            const pickedHere = allItems.filter((r) => picked.includes(r.id)).length;
            return (
              <div key={g.name} className="border-b border-slate-100 last:border-0">
                <button
                  type="button"
                  onClick={() => setOpenCats((s) => ({ ...s, [g.name]: !s[g.name] }))}
                  className="w-full flex items-center gap-2 py-2.5 text-left"
                  data-testid={`picker-category-${g.name}`}
                >
                  <BookOpen className="h-4 w-4 text-brand-blue shrink-0" />
                  <span className="flex-1 text-sm font-medium text-slate-900">{g.name}</span>
                  <VisTag staff={g.staff} staffCount={g.staffCount} testId={`picker-category-vis-${g.name}`} />
                  {pickedHere > 0 && (
                    <span className="bg-brand text-white text-[10px] font-bold rounded-full px-1.5">{pickedHere}</span>
                  )}
                  <span className="text-[11px] text-slate-400">{g.total}</span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="pb-2 pl-6">
                    {g.subs.map((s) => {
                      const subOpen = searching || openSubs[s.id];
                      const pickedSub = s.items.filter((r) => picked.includes(r.id)).length;
                      return (
                        <div key={s.id}>
                          <button
                            type="button"
                            onClick={() => setOpenSubs((o) => ({ ...o, [s.id]: !o[s.id] }))}
                            className="w-full flex items-center gap-2 py-1.5 text-left"
                            data-testid={`picker-subcategory-${s.id}`}
                          >
                            <FolderOpen className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <span className="flex-1 text-[13px] font-medium text-slate-700">{s.name}</span>
                            <VisTag staff={s.staff} staffCount={s.staffCount} testId={`picker-subcategory-vis-${s.id}`} />
                            {pickedSub > 0 && (
                              <span className="bg-brand text-white text-[10px] font-bold rounded-full px-1.5">{pickedSub}</span>
                            )}
                            <span className="text-[11px] text-slate-400">{s.items.length}</span>
                            <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${subOpen ? "rotate-180" : ""}`} />
                          </button>
                          {subOpen && <div className="pl-5">{renderItems(s.items)}</div>}
                        </div>
                      );
                    })}
                    {renderItems(g.loose)}
                  </div>
                )}
              </div>
            );
          })}
          {groups.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Sin recursos disponibles</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => onConfirm(picked)} disabled={picked.length === 0} data-testid="confirm-attach-resources">
            Agregar {picked.length > 0 ? `(${picked.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResourcePicker;
